<template>
  <div class="zhifutd-pay-root">
    <div v-if="payLoading" class="pay-loading-mask" aria-live="polite">
      <div class="pay-loading-card">
        <p class="pay-loading-title">正在连接支付渠道</p>
        <p class="pay-loading-sub">通道准备中，请勿关闭本页…</p>
        <div class="pay-loading-bar"><div class="pay-loading-bar-inner"></div></div>
      </div>
    </div>

    <div v-if="pageBooting" class="page">
      <div class="brand">收银台</div>
      <div class="brand-sub">加载中…</div>
    </div>

    <div v-show="!pageBooting" class="page">
      <div class="brand">收银台</div>
      <div class="brand-sub">支付宝收款</div>

      <div class="card">
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountYuan }}</span>
        </div>

        <div v-if="orderCountdownVisible" class="countdown-row">
          订单剩余 <span class="countdown-val">{{ orderCountdownText }}</span>
        </div>

        <p v-if="hintMain" class="hint">{{ hintMain }}</p>
        <p v-if="hintErr" class="hint err">{{ hintErr }}</p>

        <div v-show="showQrBlock" class="qr-wrap">
          <div class="qr-shell">
            <div ref="qrHost"></div>
          </div>
          <p class="qr-guide">打开「扫一扫」</p>
        </div>

        <div v-if="showPayButton" class="actions">
          <button type="button" class="btn-pay" :disabled="btnDisabled" @click="handlePayClick">立即支付</button>
        </div>

        <div class="meta">
          <div class="meta-row"><span>商户订单号</span><span class="meta-val">{{ mchOrderNo || '—' }}</span></div>
          <div class="meta-row"><span>下单时间</span><span class="meta-val">{{ createTime || '—' }}</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatCountdown,
  isDesktopBrowser,
  isExecutePayloadReady,
  loadPageContext,
  pickQrText,
  renderQrToElement,
  requestExecute,
  tryZhifutdHalfScreen
} from '@/utils/zhifutdCashierCore'
import { redirectCashierExpiredPay } from '@/utils/unifiedCashierCore'

const STYLE_ID = 'zhifutd-pay-stylesheet'
const LOADING_SAFETY_MS = 90000

export default {
  name: 'ZhifutdPay',
  data () {
    return {
      pageBooting: true,
      payLoading: false,
      orderId: '',
      token: '',
      amountYuan: '0.00',
      mchOrderNo: '',
      createTime: '',
      expireAtMillis: null,
      invalidToken: false,
      wrongChannel: false,
      expired: false,
      hintMain: '请稍候',
      hintErr: '',
      latestPayload: null,
      qrVisible: false,
      orderTimer: null,
      safetyTimer: null,
      orderCountdownText: '--:--:--',
      desktop: false
    }
  },
  computed: {
    orderCountdownVisible () {
      return !this.invalidToken && !this.wrongChannel && !this.expired && this.expireAtMillis
    },
    btnDisabled () {
      return this.payLoading || this.invalidToken || this.wrongChannel || this.expired
    },
    showQrBlock () {
      return this.desktop && this.qrVisible
    },
    showPayButton () {
      return !this.desktop && !this.invalidToken && !this.wrongChannel && !this.expired
    }
  },
  mounted () {
    document.title = '收银台'
    this.ensureStyles()
    this.desktop = isDesktopBrowser()
    this.orderId = String(this.$route.params.orderId || '')
    this.token = String(this.$route.query.t || '')
    this.boot()
  },
  beforeDestroy () {
    this.clearTimers()
  },
  methods: {
    ensureStyles () {
      if (document.getElementById(STYLE_ID)) return
      const link = document.createElement('link')
      link.id = STYLE_ID
      link.rel = 'stylesheet'
      const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
      link.href = `${base}css/zhifutd-pay.css`
      document.head.appendChild(link)
    },
    clearTimers () {
      if (this.orderTimer) clearInterval(this.orderTimer)
      if (this.safetyTimer) clearTimeout(this.safetyTimer)
      this.orderTimer = null
      this.safetyTimer = null
    },
    setLoading (on) {
      if (this.safetyTimer) {
        clearTimeout(this.safetyTimer)
        this.safetyTimer = null
      }
      this.payLoading = !!on
      if (on) {
        this.safetyTimer = setTimeout(() => {
          this.payLoading = false
          this.hintErr = '支付请求超时，请稍后重试'
        }, LOADING_SAFETY_MS)
      }
    },
    startOrderCountdown () {
      if (!this.expireAtMillis || this.expired) return
      const tick = () => {
        const left = this.expireAtMillis - Date.now()
        if (left <= 0) {
          this.orderCountdownText = '00:00:00'
          this.expired = true
          this.hintErr = '订单已超时，请重新下单'
          return
        }
        this.orderCountdownText = formatCountdown(left)
      }
      tick()
      this.orderTimer = setInterval(tick, 1000)
    },
    async applyPayload (d) {
      this.latestPayload = d
      this.hintErr = ''
      if (!isExecutePayloadReady(d)) {
        this.hintErr = (d && d.message) || '未获取到支付链接'
        return false
      }
      if (this.desktop) {
        this.hintMain = '请扫描下方二维码完成支付'
        this.qrVisible = true
        await this.$nextTick()
        const ok = await renderQrToElement(this.$refs.qrHost, pickQrText(d))
        if (!ok) this.hintErr = '二维码渲染失败，请刷新重试'
        return true
      }
      this.qrVisible = false
      this.hintMain = '请点击下方「立即支付」完成付款'
      return true
    },
    async doExecute (jumpIfMobile) {
      this.setLoading(true)
      this.hintErr = ''
      try {
        const d = await requestExecute(this.orderId, this.token)
        if (d.expired) {
          this.expired = true
          redirectCashierExpiredPay()
          return
        }
        const ok = await this.applyPayload(d)
        if (ok && jumpIfMobile && !this.desktop) {
          await tryZhifutdHalfScreen(this.latestPayload)
        }
      } catch (e) {
        if (e && e.expiredPayload) {
          this.expired = true
          redirectCashierExpiredPay()
          return
        }
        this.hintErr = (e && e.message) || '出链失败，请重试'
      } finally {
        this.setLoading(false)
      }
    },
    async handlePayClick () {
      if (this.expired || this.invalidToken || this.wrongChannel || this.payLoading) return
      await this.doExecute(true)
    },
    async boot () {
      try {
        const ctx = await loadPageContext(this.orderId, this.token)
        this.amountYuan = ctx.amountYuan || '0.00'
        this.mchOrderNo = ctx.mchOrderNo || ''
        this.createTime = ctx.createTime || ''
        this.expireAtMillis = ctx.expireAtMillis
        this.invalidToken = !!ctx.invalidToken
        this.wrongChannel = !!ctx.wrongChannel
        this.expired = !!ctx.expired
        if (this.invalidToken) {
          this.hintMain = ''
          this.hintErr = '链接无效或已失效'
          return
        }
        if (this.wrongChannel) {
          this.hintMain = ''
          this.hintErr = '非直付通科技订单，请使用正确支付链接'
          return
        }
        if (this.expired) {
          this.hintMain = ''
          this.hintErr = '订单已超时，请重新下单'
          return
        }
        this.startOrderCountdown()
        if (this.desktop) {
          if (isExecutePayloadReady(ctx)) {
            await this.applyPayload(ctx)
            return
          }
          this.hintMain = '正在生成支付二维码…'
          this.pageBooting = false
          await this.doExecute(false)
          return
        }
        this.hintMain = '请点击下方「立即支付」完成付款'
      } catch (e) {
        this.hintErr = (e && e.message) || '页面加载失败'
      } finally {
        this.pageBooting = false
      }
    }
  }
}
</script>
